import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  AppContainer,
  MainContentContainer,
  BodyContainer,
} from '../../resources/styling/appStyle';
import { SideMenu } from '../../components/layouts/SideMenu';
import { Header } from '../../components/layouts/Header';
import { config } from '../../utils/config';
import identityAdminService from '../../services/api/identityAdminService';

function buildSuccessMessage(result) {
  if (result == null) return 'Email sent.';
  if (typeof result.hasExistingPassword === 'boolean') {
    if (result.hasExistingPassword) {
      return (
        <>
          <strong>Password reset</strong> — We emailed them a link to choose a new password. Ask them to
          check their inbox (and spam).
        </>
      );
    }
    return (
      <>
        <strong>Invite</strong> — We emailed them a link to set up their account. Ask them to check
        their inbox (and spam).
      </>
    );
  }
  return result.message || 'Email sent.';
}

export default function SendSetPasswordLinkUtility() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const apiConfigured = Boolean(config.IDENTITY_API_URL);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    setLoading(true);
    try {
      const data = await identityAdminService.sendSetPasswordLink({ email });
      setResult(data);
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppContainer>
      <SideMenu />
      <BodyContainer>
        <Header />
        <MainContentContainer>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant='h5' component='h1' gutterBottom>
                Send invite or password reset
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 520 }}>
                Enter the user&apos;s email. They get an invite if they have no password yet, or a
                reset link if they already have one.
              </Typography>
            </Grid>

            {!apiConfigured && (
              <Grid item xs={12}>
                <Alert severity='error'>
                  Identity API URL is not set for this environment (REACT_APP_IDENTITY_API_URL).
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} md={8} lg={6}>
              <Paper sx={{ p: 3 }} component='form' onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label='Email'
                      type='email'
                      value={email}
                      onChange={(ev) => setEmail(ev.target.value)}
                      disabled={loading || !apiConfigured}
                      placeholder='name@school.org'
                      autoComplete='off'
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box display='flex' alignItems='center' gap={2}>
                      <Button
                        type='submit'
                        variant='contained'
                        disabled={loading || !apiConfigured}
                      >
                        Send email
                      </Button>
                      {loading && <CircularProgress size={24} />}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {error && (
              <Grid item xs={12} md={8}>
                <Alert severity='error'>{error}</Alert>
              </Grid>
            )}

            {result && (
              <Grid item xs={12} md={8}>
                <Alert severity='success'>
                  <Typography variant='body2' component='div'>
                    {buildSuccessMessage(result)}
                  </Typography>
                  {result.email ? (
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                      Sent to: {result.email}
                    </Typography>
                  ) : null}
                </Alert>
              </Grid>
            )}
          </Grid>
        </MainContentContainer>
      </BodyContainer>
    </AppContainer>
  );
}
